import type { Grabber } from './Grabbers';
import type { HandRole } from './Handedness';
import type { Mode } from '../possession/ToolMode';
import { MODE_CONTROL_TEXT } from './ModeDefinitions';
import type { ModeControlText } from './ModeDefinition';

// spec: SPEC-078

/**
 * The single source of truth for "what does this control do" — CLAUDE.md's task brief for
 * `ControlHintsPanel`/`ControlHintsController`: *"it must be derived, not described in
 * prose."* Everything that explains the controls (the look-at panel; the portal's pre-entry
 * controls page, `PortalControls.ts`) reads from this module rather than restating the
 * bindings by hand — the same failure `plan/grid.md` names directly: recentre was removed and
 * still lingered in prose documents hours later, and a hand-written prompt fed to Gemma would
 * have taught a binding that no longer existed.
 *
 * **`MODE_BINDINGS` is now derived, not authored — the direction `plan/mode-hierarchy.md`'s
 * "Hints derive from the mode data, down the road" section committed to.** Every dominant-hand
 * binding whose meaning depends on the current mode (the trigger, and either stick axis) is
 * built from `MODE_CONTROL_TEXT` (`ModeDefinitions.ts`), the same static text
 * `buildModeDefinitions` spreads into the live `ModeDefinition.label`/`triggerDescription` used
 * by the mode ring — so there is exactly one written copy of what a mode's trigger or stick
 * axis does, not a hand-maintained second copy here that could drift from it the way this
 * file's own history already did once (see `modeBindingsFor`'s own doc for the one deliberate
 * exception that is *not* derived this way).
 *
 * **`ALWAYS_BINDINGS` stays hand-authored, deliberately.** Everything in it — the off hand's
 * whole repertoire, plus the dominant grip/stick mode-switch gesture — is true in *every* mode
 * by construction, and `ModeDefinition` (`ModeDefinition.ts`'s own doc) is deliberately scoped
 * to "what one mode looks and feels like from the outside," not to the chrome that surrounds
 * every mode identically. There is no mode-data object these bindings could be derived *from*
 * without inventing one that would exist solely to be read back out here — see this file's own
 * doc on `ALWAYS_BINDINGS` for the fuller reasoning.
 *
 * **Two things are genuinely checkable, and this module is built so a test can check them —
 * see `ControlBindings.test.ts`:**
 *
 * 1. Every `control` named below is a real field on `Grabber`. `ControlName` is intersected
 *    against `keyof Grabber` at the type level (`ControlNamesExistOnGrabber` below), so
 *    renaming or removing a `Grabber` field this module still names fails `npm run typecheck`
 *    outright — not a lint warning, a build failure. The runtime test goes one step further
 *    and constructs a real `Grabber`-shaped fixture, asserting each named control is actually
 *    present on it (not merely present in the *type*, which a stale `as` cast could fake).
 * 2. Every `Mode` has an entry. `MODE_BINDINGS` is typed `Record<Mode, ...>`, so adding a
 *    fourth tool to `ToolMode.ts` without adding a row here — meaning without adding a row to
 *    `MODE_CONTROL_TEXT`, its actual source — fails to compile: TypeScript's own exhaustiveness
 *    check on a `Record` keyed by a union, not a hand-maintained list that could itself drift
 *    from `Mode`. `ModeDefinitions.test.ts`'s own "a fifth mode needs only a definition" is the
 *    live proof that a new mode requires no edit to this file at all.
 *
 * What is **not** checkable, and is not pretended to be: that a `description` string is
 * actually *true* of the gesture it names. Nothing here can verify prose against behaviour —
 * that has to come from reading the source at the time `MODE_CONTROL_TEXT`/`ALWAYS_BINDINGS`
 * are written (and updating them by hand whenever a binding changes), the same limit
 * `HubContractTests` has for the wire contract it mirrors (CLAUDE.md: "a one-directional
 * tripwire"). What the two checks above catch is the cheaper, more common failure: a control or
 * a mode disappearing entirely and the table not noticing.
 */

/**
 * The physical controls a binding can name. Restricted to fields `Grabber` actually exposes as
 * a discrete "thing you can press or feel" — `position`/`rotation`/`aimOrigin`/`forward`/
 * `aimRotation`/`tracked`/`role` describe a pose or capability, not a control, so they are
 * deliberately excluded even though they are also `keyof Grabber`.
 */
export type ControlName = 'near' | 'far' | 'stick' | 'talkButton' | 'micToggleButton';

// Compile-time guard: every member of ControlName must be assignable to keyof Grabber. A union
// only extends another union when *every* member does, so renaming or deleting any one of the
// fields above on `Grabber` makes this fail to compile — see this file's own doc, point 1.
type ControlNamesExistOnGrabber = ControlName extends keyof Grabber ? true : never;
// Never read; its only job is to exist as a place for the type above to be checked against
// `true`, which fails to compile if `ControlNamesExistOnGrabber` above ever resolves to `never`.
const controlNamesExistOnGrabber: ControlNamesExistOnGrabber = true;
void controlNamesExistOnGrabber;

export interface ControlBinding {
  /** Which physical hand this control lives on — resolved through `Grabbers.byRole`, never a
   *  literal left/right, so left-handed mode needs no second copy of this table. */
  readonly hand: HandRole;

  readonly control: ControlName;

  /** Short label for the physical action — "Grip", "Stick up/down" — not the effect. */
  readonly gesture: string;

  /** What it does, in plain words, in whatever mode this binding is listed under. */
  readonly description: string;
}

/**
 * Bindings that mean the same thing in every `Mode`. The off hand's whole job — present the
 * tray, move the body, talk to the room — never changes shape when the dominant hand switches
 * tools (`bindOffHandControls`, `PaletteUI`'s stick navigation and dwell/trigger commit, and
 * `AssistantController.tick` all read the off hand with no `ToolMode` check anywhere), so
 * every off-hand entry lives here rather than being repeated identically under all three
 * modes. The one dominant-hand entry here — which way the stick's *left/right* axis switches
 * tools — is the exception: `AuthoringController.tickToolCycle` reads it identically
 * regardless of the tool already selected, where the *up/down* axis's meaning genuinely does
 * depend on the mode and so lives in `MODE_BINDINGS` instead.
 */
export const ALWAYS_BINDINGS: readonly ControlBinding[] = [
  {
    hand: 'off',
    control: 'near',
    gesture: 'Grip',
    description: 'Open tray, or its readout.',
  },
  {
    hand: 'off',
    control: 'stick',
    gesture: 'Stick',
    description: 'Steer palette, or travel.',
  },
  {
    hand: 'off',
    control: 'far',
    gesture: 'Trigger',
    description: 'Confirm tray item.',
  },
  {
    hand: 'off',
    control: 'talkButton',
    gesture: 'Face button',
    description: 'Hold to talk to Gemma.',
  },
  {
    hand: 'off',
    control: 'micToggleButton',
    gesture: 'Other face button',
    description: 'Toggle mic.',
  },
  {
    hand: 'dominant',
    control: 'near',
    gesture: 'Grip',
    description: 'Hold to show mode ring.',
  },
  {
    hand: 'dominant',
    control: 'stick',
    gesture: 'Grip+stick L/R',
    description: 'Switch mode.',
  },
];

/** The fixed physical-gesture label for each dominant-hand control `modeBindingsFor` can
 *  produce — the same three strings regardless of which mode is asking, so they live here once
 *  rather than being repeated inside `MODE_CONTROL_TEXT`'s own per-mode entries. What varies by
 *  mode is only what the control *does* (`ModeControlText`'s own fields); what to *call* it does
 *  not. */
const TRIGGER_GESTURE = 'Trigger';
const STICK_X_GESTURE = 'Stick L/R';
const STICK_Y_GESTURE = 'Stick up/down';

/**
 * Build's own hold-the-node-and-speak-a-label gesture (`NodeLabelController.tick`, read off
 * `dominant.talkButton`) is the one dominant-hand binding no `ModeDefinition` names anywhere —
 * deliberately: it is gated on *holding a node*, not on being in a particular mode
 * (`ModeDefinition.ts`'s own doc: "nothing to say about... how its gesture is implemented"), and
 * in practice only ever fires in Build because grabbing a node at all is build-only
 * (`PossessionManager.considerGrab`'s `isToolActive` gate). Hand-authored here rather than
 * derived from mode data — the one place this file still restates a fact instead of reading it
 * off `MODE_CONTROL_TEXT`, and named as an accepted exception rather than left to look like an
 * oversight.
 */
const BUILD_ONLY_EXTRA_BINDINGS: readonly ControlBinding[] = [
  {
    hand: 'dominant',
    control: 'talkButton',
    gesture: 'Face button',
    description: 'Hold, speak to label.',
  },
];

/** Builds one mode's own dominant-hand rows from `text` — the trigger always, each stick axis
 *  only when `text` says it is bound, plus Build's own hand-authored extra
 *  (`BUILD_ONLY_EXTRA_BINDINGS`). Exported, and taking `text` as a parameter rather than closing
 *  over the real `MODE_CONTROL_TEXT`, specifically so a test can hand it a hypothetical entry
 *  and prove a mode this file has never heard of still produces a correct row — see
 *  `ControlBindings.test.ts`, "a hypothetical mode". */
export function modeBindingsFor(mode: Mode, text: ModeControlText): readonly ControlBinding[] {
  const bindings: ControlBinding[] = [
    { hand: 'dominant', control: 'far', gesture: TRIGGER_GESTURE, description: text.triggerDescription },
  ];

  if (text.stickXDescription !== null) {
    bindings.push({ hand: 'dominant', control: 'stick', gesture: STICK_X_GESTURE, description: text.stickXDescription });
  }

  if (text.stickYDescription !== null) {
    bindings.push({ hand: 'dominant', control: 'stick', gesture: STICK_Y_GESTURE, description: text.stickYDescription });
  }

  if (mode === 'build') {
    bindings.push(...BUILD_ONLY_EXTRA_BINDINGS);
  }

  return bindings;
}

/** Builds a full `MODE_BINDINGS`-shaped table from any `Record<Mode, ModeControlText>` —
 *  exported for the identical testability reason `modeBindingsFor` takes `text` as a parameter,
 *  and what `MODE_BINDINGS` below is actually built from. */
export function buildModeBindings(
  text: Readonly<Record<Mode, ModeControlText>>,
): Readonly<Record<Mode, readonly ControlBinding[]>> {
  const entries = Object.keys(text) as Mode[];
  const result = {} as Record<Mode, readonly ControlBinding[]>;

  for (const mode of entries) {
    result[mode] = modeBindingsFor(mode, text[mode]);
  }

  return result;
}

/**
 * Bindings whose meaning depends on which tool the dominant hand currently has selected
 * (`ToolMode.mode`) — derived from `MODE_CONTROL_TEXT` (`ModeDefinitions.ts`), not hand-typed
 * (see this file's own module doc). Still typed `Record<Mode, ...>` via `buildModeBindings`'s
 * own signature, so the type system — not a runtime scan — is what proves every mode has a row
 * (see this file's own doc, point 2): a mode added to `Mode` without a matching
 * `MODE_CONTROL_TEXT` entry fails to compile before this ever runs.
 */
export const MODE_BINDINGS: Readonly<Record<Mode, readonly ControlBinding[]>> = buildModeBindings(MODE_CONTROL_TEXT);

/** Every `Mode` there is, derived from `MODE_BINDINGS`'s own keys rather than a second,
 *  hand-copied list — `Object.keys` on a `Record<Mode, ...>` is exactly the `Mode` union at
 *  runtime, so this can never drift from the compile-time exhaustiveness check above. */
export const ALL_MODES = Object.keys(MODE_BINDINGS) as readonly Mode[];

/** Every binding that applies to `hand` while `mode` is current — what the look-at panel
 *  shows for one hand. `ALWAYS_BINDINGS` first, then this mode's own — carrying/holding/tray
 *  concerns before the tool-specific ones, roughly the order a newcomer would want to read
 *  them in. `modeBindings` defaults to the real, derived `MODE_BINDINGS`; a test can pass a
 *  substituted table the same way `AuthoringController.controlModes.test.ts` substitutes a
 *  whole `ModeRegistry` to prove a fifth mode needs no source change here either. */
export function bindingsForHand(
  mode: Mode,
  hand: HandRole,
  modeBindings: Readonly<Record<Mode, readonly ControlBinding[]>> = MODE_BINDINGS,
): readonly ControlBinding[] {
  return [...ALWAYS_BINDINGS, ...(modeBindings[mode] ?? [])].filter((binding) => binding.hand === hand);
}
